import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { ActivitySubjectThreadItem } from '@shared/types/activity-feed';
import type { AccountThreadLightView, ThreadTopicSummary } from '@shared/types/thread-work';
import { readWorkHistory, type WorkHistoryCursor } from '@/lib/work-history';
import { cn } from '@/lib/utils';
import { IN_SURFACE_CLASS } from '@/lib/views';
import { ThreadCurrentResult } from './ThreadTimeline';
import { whenDone } from './work-done';

/** The last fallen dominos shown at once; the earlier ones open on demand. */
const SHOWN_DONE = 4;

const CARD = 'flex min-w-0 flex-1 basis-40 flex-col gap-0.5 rounded-lg border px-3 py-2 text-left text-xs';

const nextLabel = (topic: ThreadTopicSummary) => topic.proposal?.ctaLabel ?? topic.title;

/** One fact per run or task: the history and the read model may both carry it. */
const factKey = (item: ActivitySubjectThreadItem) => item.taskActionRunId
  ? `task:${item.taskActionRunId}`
  : item.kind === 'run' && item.sourceRunId ? `run:${item.sourceRunId}` : item.id;

/**
 * « La chaîne des comptes » (21/10/2026), the account's history since 26/10:
 * the dominos that fell (what was done on it, dated, by whom) and the one
 * falling (Domino at work). The next one is « À faire » below. Each fact
 * appears once and opens in place, under the chain, with its result; there is
 * no separate « Activité du compte » list any more.
 */
export const AccountChain = ({ items, threadState, subjectId }: {
  items: ActivitySubjectThreadItem[];
  threadState: Pick<AccountThreadLightView, 'currentTopic' | 'otherTopics'> | null;
  subjectId: string;
}) => {
  const location = useLocation();
  const [history, setHistory] = useState<ActivitySubjectThreadItem[]>([]);
  const [cursor, setCursor] = useState<WorkHistoryCursor | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [all, setAll] = useState(false);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const now = new Date();

  useEffect(() => {
    let alive = true;
    setError(null);
    void readWorkHistory(subjectId, null)
      .then((page) => { if (alive) { setHistory(page.items); setCursor(page.nextCursor); } })
      .catch((cause) => { if (alive) setError(cause instanceof Error ? cause.message : 'L’histoire du compte n’a pas pu être chargée.'); });
    return () => { alive = false; };
  }, [subjectId, items]);

  const done = useMemo(() => [...new Map([...history, ...items].filter((item) => item.eventAt).map((item) => [factKey(item), item])).values()]
    .sort((left, right) => (left.eventAt ?? '').localeCompare(right.eventAt ?? '') || left.id.localeCompare(right.id)), [history, items]);

  // A link to a task's result (#task-action-…) opens that fact, once per link.
  const openedHash = useRef<string | null>(null);
  useEffect(() => {
    if (!location.hash || openedHash.current === location.hash) return;
    const hit = done.find((item) => item.taskActionRunId && location.hash === `#task-action-${item.taskActionRunId}`);
    if (hit) { openedHash.current = location.hash; setOpenKey(factKey(hit)); setAll(true); }
  }, [done, location.hash]);

  useEffect(() => {
    if (!openKey) return undefined;
    const close = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpenKey(null); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [openKey]);

  const loadEarlier = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await readWorkHistory(subjectId, cursor);
      setHistory((previous) => [...previous, ...page.items]);
      setCursor(page.nextCursor);
    } catch {
      setError('Les faits précédents n’ont pas pu être chargés.');
    } finally {
      setLoadingMore(false);
    }
  };

  const shown = all ? done : done.slice(-SHOWN_DONE);
  const topics = threadState ? [threadState.currentTopic, ...threadState.otherTopics].filter((topic): topic is ThreadTopicSummary => topic !== null) : [];
  const running = topics.filter((topic) => topic.visibleState === 'running');
  const opened = openKey ? done.find((item) => factKey(item) === openKey) ?? null : null;
  if (done.length === 0 && running.length === 0) return error ? <p role="alert" className="mb-6 text-sm text-domino-inkSoft">{error}</p> : null;

  return (
    <div className="mb-6">
      <section aria-label="Chaîne du compte" className="flex flex-wrap items-stretch gap-2">
        {((!all && done.length > SHOWN_DONE) || (all && cursor)) && (
          <button type="button" disabled={loadingMore}
            onClick={() => (all ? void loadEarlier() : setAll(true))}
            className="rounded-lg border border-domino-cardBorder px-3 py-2 text-xs text-domino-aubergine hover:bg-domino-muted disabled:opacity-60">
            {all ? 'Plus tôt' : `+${done.length - SHOWN_DONE} plus tôt`}
          </button>
        )}
        {shown.map((item) => {
          const failed = item.tone === 'critical';
          const key = factKey(item);
          const isOpen = key === openKey;
          return (
            <button key={key} type="button" aria-expanded={isOpen}
              onClick={() => setOpenKey(isOpen ? null : key)}
              className={cn(CARD, 'transition-[border-color,transform] duration-150 ease-snap active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune',
                isOpen ? 'border-emerald-700 bg-emerald-50' : 'border-transparent bg-emerald-50 hover:border-emerald-200')}>
              <span className={cn('font-semibold', failed ? 'text-domino-inkSoft' : 'text-emerald-800')}>{failed ? 'Échec · ' : '✓ '}{item.title}</span>
              <span className="text-domino-inkSoft">{whenDone(item.eventAt!, now)}{item.actorLabel && !/^[0-9a-f-]{36}$/i.test(item.actorLabel) ? ` · ${item.actorLabel}` : ''}</span>
            </button>
          );
        })}
        {running.map((topic) => (
          <div key={topic.id} className={cn(CARD, 'border-domino-ink bg-domino-white')}>
            <span className="font-semibold text-domino-ink">{nextLabel(topic)}</span>
            <span className="flex items-center gap-1.5 text-domino-inkSoft">
              <span className="h-2 w-2 rounded-full border border-domino-ink bg-domino-sun motion-safe:animate-pulse" aria-hidden />
              Domino travaille
            </span>
          </div>
        ))}
      </section>
      {opened && (
        <div className={`mt-3 rounded-2xl border border-domino-cardBorder bg-domino-white px-5 motion-safe:animate-fadeIn md:px-6 [&>article]:border-t-0 ${IN_SURFACE_CLASS}`}>
          <ThreadCurrentResult key={factKey(opened)} item={opened} focused subjectId={subjectId} />
        </div>
      )}
      {error && <p role="alert" className="mt-2 text-xs text-domino-inkSoft">{error}</p>}
    </div>
  );
};
